
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function CreatingPersonalVisionMap() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 bg-slate-800 text-white rounded-full text-sm mb-4">
              Purpose
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
              Creating Your Personal Vision Map
            </h1>
            <div className="flex items-center justify-center text-slate-500 text-sm mb-6">
              <span>December 5, 2024</span>
              <span className="mx-2">•</span>
              <span>6 min read</span>
              <span className="mx-2">•</span>
              <span>By Elbert C. Alexander</span>
            </div>
          </div>
          
          <img 
            src="https://public.readdy.ai/ai/img_res/be6ebf6172a20a850378ad02d395def1.jpg"
            alt="Creating Your Personal Vision Map"
            className="w-full h-64 md:h-80 object-cover object-top rounded-lg shadow-lg mb-8"
          />
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 mb-8 font-medium">
              Step-by-step guide to clarifying your life's direction and creating a roadmap for meaningful achievement. 
              Transform dreams into actionable plans that honor your purpose and faith.
            </p>

            <p className="text-lg text-slate-700 mb-6">
              Have you ever felt like you're wandering through life without a clear destination? Like you have dreams and aspirations but no concrete plan to achieve them? You're not alone. Many of us struggle with the gap between where we are and where we want to be. The solution lies in creating a personal vision map—a sacred blueprint that transforms vague hopes into clear, actionable direction.
            </p>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">What Is a Personal Vision Map?</h2>
            
            <p className="text-lg text-slate-700 mb-6">
              A personal vision map is more than a goal-setting exercise—it's a comprehensive life planning tool that aligns your deepest values, God-given talents, and heartfelt dreams into a cohesive roadmap for your future. It serves as both compass and destination, guiding your daily decisions while keeping your ultimate purpose in clear view.
            </p>

            <p className="text-lg text-slate-700 mb-6">
              Unlike simple to-do lists or New Year's resolutions, a vision map integrates the spiritual dimension of your calling with practical steps for achievement. It acknowledges that true fulfillment comes not just from accomplishing goals, but from pursuing the right goals—those that align with your divine purpose and bring glory to your Creator.
            </p>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">The Foundation: Spiritual Clarity</h2>

            <p className="text-lg text-slate-700 mb-6">
              Before you can map your future, you must understand your foundation. Every meaningful vision map begins with spiritual clarity—understanding who God created you to be and what He's calling you to accomplish. This isn't about copying someone else's success or pursuing society's definition of achievement.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-800 p-6 my-8">
              <h3 className="text-xl font-semibold text-blue-800 mb-3">Foundation Questions to Consider:</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-line text-blue-800 mt-1 mr-2"></i>
                  <span>What unique gifts and talents has God given me?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-line text-blue-800 mt-1 mr-2"></i>
                  <span>What experiences have shaped my perspective and passion?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-line text-blue-800 mt-1 mr-2"></i>
                  <span>When do I feel most alive and purposeful?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-line text-blue-800 mt-1 mr-2"></i>
                  <span>What problems in the world burden my heart?</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-line text-blue-800 mt-1 mr-2"></i>
                  <span>How do I want to be remembered?</span>
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Step 1: Clarify Your Core Values</h2>

            <p className="text-lg text-slate-700 mb-6">
              Your values are the non-negotiable principles that guide your decisions and define your character. They serve as the foundation upon which your entire vision map is built. Without clear values, you may achieve goals that ultimately leave you feeling empty or conflicted.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">Identifying Your Values Exercise:</h3>
            <p className="text-lg text-slate-700 mb-6">
              List the moments in your life when you felt most proud, fulfilled, or aligned with your purpose. What values were you honoring in those moments? Common faith-based values include integrity, service, excellence, compassion, stewardship, and authenticity. Choose your top 5-7 core values and write a brief definition of what each means to you personally.
            </p>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Step 2: Define Your Life Domains</h2>

            <p className="text-lg text-slate-700 mb-6">
              A comprehensive vision map addresses all areas of your life, not just career or financial goals. Consider these key domains:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  <i className="ri-heart-line text-blue-800 mr-2"></i>
                  Spiritual Growth
                </h3>
                <p className="text-slate-600 text-sm">Your relationship with God, spiritual disciplines, faith development</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  <i className="ri-group-line text-blue-800 mr-2"></i>
                  Relationships
                </h3>
                <p className="text-slate-600 text-sm">Family, marriage, friendships, community connections</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  <i className="ri-briefcase-line text-blue-800 mr-2"></i>
                  Career & Purpose
                </h3>
                <p className="text-slate-600 text-sm">Professional calling, ministry, entrepreneurship, impact</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  <i className="ri-heart-pulse-line text-blue-800 mr-2"></i>
                  Health & Wellness
                </h3>
                <p className="text-slate-600 text-sm">Physical health, mental wellness, life balance</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  <i className="ri-graduation-cap-line text-blue-800 mr-2"></i>
                  Growth & Learning
                </h3>
                <p className="text-slate-600 text-sm">Personal development, skills, education, wisdom</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  <i className="ri-hand-heart-line text-blue-800 mr-2"></i>
                  Service & Legacy
                </h3>
                <p className="text-slate-600 text-sm">How you serve others, the mark you leave on the world</p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Step 3: Create Your Vision Statements</h2>

            <p className="text-lg text-slate-700 mb-6">
              For each life domain, write a vision statement that describes where you want to be in 5-10 years. These statements should be specific, inspiring, and aligned with your values. Write them in present tense, as if they're already accomplished.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">Vision Statement Framework:</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 my-6">
              <p className="text-slate-700 mb-4"><strong>Example for Career & Purpose:</strong></p>
              <p className="text-slate-700 italic">
                "I am leading a thriving organization that transforms lives through faith-based coaching and mentorship. 
                I speak at conferences worldwide, inspiring thousands to discover their God-given purpose. My books 
                and resources are helping people across generations build meaningful careers aligned with their values."
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Step 4: Break It Down Into Goals</h2>

            <p className="text-lg text-slate-700 mb-6">
              Transform each vision statement into 3-5 specific, measurable goals. These become your major milestones on the journey toward your vision. Use the SMART criteria (Specific, Measurable, Achievable, Relevant, Time-bound) but add a sixth element: Spiritual alignment.
            </p>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Step 5: Identify Your Next Steps</h2>

            <p className="text-lg text-slate-700 mb-6">
              For each goal, identify the next 2-3 concrete actions you can take in the coming month. This bridges the gap between your grand vision and your daily reality. Without this step, vision maps remain inspiring but impractical dreams.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
              <h3 className="text-xl font-semibold text-green-800 mb-3">Action Planning Tips:</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Start with actions you can complete this week</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Focus on progress, not perfection</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Build momentum with small wins</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Schedule actions in your calendar</span>
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Step 6: Create Your Visual Map</h2>

            <p className="text-lg text-slate-700 mb-6">
              Now it's time to create the visual representation of your vision map. This can be a traditional vision board with images and words, a digital mind map, or a written document with sections for each domain. The key is making it visual and accessible for regular review.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">Visual Elements to Include:</h3>
            <ul className="list-disc list-inside text-lg text-slate-700 mb-6 space-y-2">
              <li>Images that represent your vision for each life domain</li>
              <li>Key words and phrases that inspire and motivate you</li>
              <li>Scripture verses or spiritual quotes that ground your vision</li>
              <li>Milestone dates and celebration points</li>
              <li>Daily/weekly action items prominently displayed</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Living Your Vision Map</h2>

            <p className="text-lg text-slate-700 mb-6">
              Creating your vision map is just the beginning. The real transformation happens when you use it as a living document that guides your daily decisions and keeps you accountable to your highest aspirations.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">Regular Review Process:</h3>
            <ul className="list-disc list-inside text-lg text-slate-700 mb-6 space-y-2">
              <li><strong>Daily:</strong> Review your action items and make progress on at least one</li>
              <li><strong>Weekly:</strong> Assess progress across all domains and adjust actions as needed</li>
              <li><strong>Monthly:</strong> Evaluate goal progress and celebrate achievements</li>
              <li><strong>Quarterly:</strong> Review and revise your vision statements as you grow</li>
              <li><strong>Annually:</strong> Complete overhaul and renewal of your entire vision map</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Overcoming Common Obstacles</h2>

            <p className="text-lg text-slate-700 mb-6">
              Every vision map journey encounters obstacles. Here are the most common challenges and how to overcome them:
            </p>

            <div className="space-y-6 mb-8">
              <div className="border-l-4 border-red-400 pl-6">
                <h4 className="text-lg font-semibold text-slate-800 mb-2">Obstacle: Overwhelming Size of Dreams</h4>
                <p className="text-slate-700">Solution: Break everything down into smaller, manageable steps. Focus on progress, not perfection.</p>
              </div>
              <div className="border-l-4 border-red-400 pl-6">
                <h4 className="text-lg font-semibold text-slate-800 mb-2">Obstacle: Lack of Clarity</h4>
                <p className="text-slate-700">Solution: Start with what you know and refine as you go. Clarity comes through action, not just thinking.</p>
              </div>
              <div className="border-l-4 border-red-400 pl-6">
                <h4 className="text-lg font-semibold text-slate-800 mb-2">Obstacle: Fear of Failure</h4>
                <p className="text-slate-700">Solution: Reframe failure as learning. Every setback provides valuable insights for your journey.</p>
              </div>
              <div className="border-l-4 border-red-400 pl-6">
                <h4 className="text-lg font-semibold text-slate-800 mb-2">Obstacle: Inconsistent Action</h4>
                <p className="text-slate-700">Solution: Build accountability systems and start with tiny, consistent actions rather than dramatic changes.</p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">The Power of Divine Partnership</h2>

            <p className="text-lg text-slate-700 mb-6">
              Remember, you're not creating and pursuing your vision map alone. When your dreams align with God's purposes, you have access to divine wisdom, strength, and provision that far exceed your natural abilities. Make prayer and spiritual discernment integral parts of your vision mapping process.
            </p>

            <p className="text-lg text-slate-700 mb-6">
              "For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future." (Jeremiah 29:11). Your vision map is a way of partnering with God to discover and fulfill those plans.
            </p>

            <h2 className="text-2xl font-bold text-slate-800 mb-4 mt-8">Start Today</h2>

            <p className="text-lg text-slate-700 mb-6">
              Creating your personal vision map doesn't require perfect conditions or complete clarity. It requires courage to begin and commitment to continue. Start where you are, use what you have, and trust that each step forward brings greater clarity and confidence.
            </p>

            <p className="text-lg text-slate-700 mb-6">
              Your life has incredible potential. Your dreams matter. Your calling is significant. Don't let another year pass without a clear map for getting where God is calling you to go. The world needs what you have to offer, but first, you need to get clear on what that is and how to develop it.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Ready to Create Your Vision Map?</h3>
              <p className="text-slate-700 mb-4">
                "The Dream Journal" provides structured exercises, reflection prompts, and spiritual insights to guide you through creating your personal vision map. Don't navigate this journey alone—let this resource be your companion in discovering and pursuing your God-given purpose.
              </p>
              <Link to="/shop">
                <Button className="bg-blue-800 hover:bg-blue-900">
                  Get Your Dream Journal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Newsletter Signup */}
      <section className="py-16 bg-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Start Your Transformation Journey
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get exclusive insights and practical tools for personal growth delivered to your inbox.
          </p>
          <form 
            id="newsletter-signup-vision-map"
            action="https://readdy.ai/api/form/submit/newsletter-signup-vision-map"
            method="POST"
            data-readdy-form
            className="max-w-md mx-auto flex flex-col sm:flex-row gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              
              fetch(form.action, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData as any).toString()
              })
              .then(response => response.json())
              .then(data => {
                if (data.success) {
                  alert('Thank you for subscribing! Check your email for confirmation.');
                  form.reset();
                } else {
                  alert('Something went wrong. Please try again.');
                }
              })
              .catch(error => {
                console.error('Error:', error);
                alert('Something went wrong. Please try again.');
              });
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              required
              className="flex-1 px-4 py-3 rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800"
            />
            <button
              type="submit"
              className="whitespace-nowrap cursor-pointer px-6 py-3 bg-white text-blue-800 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
